import { useEffect, useRef, useCallback } from "react";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import type { SerializedEditorState } from "lexical";
import { extractPlainText, countText } from "../utils/serialization";

export interface AutoSavePluginProps {
  onSave: (editorState: SerializedEditorState, wordCount: number, characterCount: number) => void;
  interval?: number;
}

export function AutoSavePlugin({ onSave, interval = 5000 }: AutoSavePluginProps) {
  const [editor] = useLexicalComposerContext();
  const lastSavedRef = useRef<string>("");
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const saveContent = useCallback(() => {
    const editorState = editor.getEditorState();
    const serialized = editorState.toJSON();
    const serializedString = JSON.stringify(serialized);

    if (serializedString !== lastSavedRef.current) {
      const plainText = extractPlainText(serialized);
      const { words, characters } = countText(plainText);
      onSave(serialized, words, characters);
      lastSavedRef.current = serializedString;
    }
  }, [editor, onSave]);

  useEffect(() => {
    return editor.registerUpdateListener(({ dirtyElements, dirtyLeaves }) => {
      if (dirtyElements.size === 0 && dirtyLeaves.size === 0) {
        return;
      }

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(saveContent, interval);
    });
  }, [editor, saveContent, interval]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        // Flush pending save on unmount to prevent data loss (e.g., tab switch)
        saveContent();
      }
    };
  }, [saveContent]);

  return null;
}
